<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'companies',
    path: '/companies',
    component: 'CompaniesView',
    params: [
      {
        name: 'role',
        prop: 'role',
      },
    ],
  });
  addRoute({
    name: 'ownCompanies',
    path: '/own-companies',
    component: 'CompaniesView',
    componentProps: [
      {
        name: 'role',
        value: 'own',
      },
    ],
    title: () => pgettext('Companies', 'Own companies'),
  });
  addRoute({
    name: 'partners',
    path: '/partners',
    component: 'CompaniesView',
    componentProps: [
      {
        name: 'role',
        value: 'partner',
      },
    ],
    title: () => pgettext('Companies', 'Partners'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CompaniesView',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    role: {
      type: String,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const messages = ref({
    titles: {
      own: $fn.value.pgettext('Companies', 'Own companies'),
      partner: $fn.value.pgettext('Companies', 'Partners'),
    },
    create: {
      own: $fn.value.pgettext('Companies|Command', 'New company...'),
      partner: $fn.value.pgettext('Companies|Command', 'New partner...'),
    },
  });
  const selectedItems = ref([]);
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      id: 'code',
      field: 'code',
      name: $fn.value.pgettext('Companies', 'Code'),
      formatter: {
        name: 'routeLink',
        to: 'company',
        params: {
          id: 'id',
        },
      },
    },
    {
      id: 'name',
      field: 'name',
      name: $fn.value.pgettext('Companies', 'Name'),
      formatter: {
        name: 'routeLink',
        to: 'company',
        params: {
          id: 'id',
        },
      },
    },
    {
      id: 'tax_number',
      field: 'tax_number',
      name: $fn.value.pgettext('Companies', 'Tax number'),
    },
    {
      id: 'registration_number',
      field: 'registration_number',
      name: $fn.value.pgettext('Companies', 'Registration number'),
    },
    {
      id: 'is_natural_person',
      field: 'is_natural_person',
      name: $fn.value.pgettext('Companies', 'Natural person'),
      align: 'center',
      formatter: {
        name: 'checkbox',
      },
    },
    {
      id: 'sole_proprietor',
      field: 'sole_proprietor',
      name: $fn.value.pgettext('Companies', 'Sole proprietor'),
      align: 'center',
      formatter: {
        name: 'checkbox',
      },
    },
    {
      id: 'is_contractor',
      field: 'is_contractor',
      name: $fn.value.pgettext('Companies', 'Contractor'),
      align: 'center',
      formatter: {
        name: 'checkbox',
      },
    },
    {
      id: 'external_key',
      field: 'external_key',
      name: $fn.value.pgettext('Companies', 'External key'),
    },
  ]);

  const $data = computed(() => ({
    messages: messages.value,
    selectedItems: selectedItems.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_ds = () => {
    return $fn.value.gridDataSource('list_company', {
      parameters: { role: props.role },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'messages' },
      { name: 'selectedItems' },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      messages,
      selectedItems,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: messages.value.titles[props.role],
        icon: $config.value.company.icons[props.role],
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.role,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('role');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_o2kj1vvgx_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_g78ek200e_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_4615685607825208_showModal_08x7wwd49_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateCompany',
      props: {
        role: props.role,
      },
    };
  };

  const act_7315092382398562_closeModal_o2kj1vvgx =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_o2kj1vvgx_getActionArgs,
    });
  const inst_evh_7315092382398562_close_e5tr8xyjp =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_o2kj1vvgx,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_e5tr8xyjp],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_hisoj9ei9 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_qzd1r7jfb =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_hisoj9ei9,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_qzd1r7jfb],
    event: 'reload',
  });
  const act_2248226175642056_setData_g78ek200e = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_g78ek200e_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_37pdhurwu =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_g78ek200e,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_37pdhurwu],
      event: 'selectedRowsChanged',
    });
  const act_4615685607825208_showModal_08x7wwd49 =
    new actionTypes.ShowModalAction({
      actionArgs: act_4615685607825208_showModal_08x7wwd49_getActionArgs,
    });
  const inst_evh_4615685607825208_click_5w5km6ybg =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_showModal_08x7wwd49,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_4615685607825208_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4615685607825208_click_5w5km6ybg],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_close_e5tr8xyjp,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_qzd1r7jfb,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_37pdhurwu,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_4615685607825208_click_5w5km6ybg,
    inst_evh_4615685607825208_click,
  };

  const evh_7315092382398562_close_e5tr8xyjp = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_e5tr8xyjp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_qzd1r7jfb = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_qzd1r7jfb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_37pdhurwu = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_37pdhurwu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_click_5w5km6ybg = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_click_5w5km6ybg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_click = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CompaniesView"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpDataGrid
      name="companies"
      :headerTitle="pgettext('CompaniesView', 'Companies')"
      :items="ds"
      checkbox-row-selection
      export-to-csv
      :exportToCsvFilename="pgettext('filename', 'companies')"
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2">
          <ItpButton
            :icon="$config.company.icons[role]"
            :text="messages.create[role]"
            size="sm"
            variant="light"
            v-if="messages.create[role]"
            @click="evh_4615685607825208_click($event, {})"
          >
          </ItpButton>
          <PageHelp path="/claims/companies.html"> </PageHelp>
        </div>
      </template>
    </ItpDataGrid>
  </ItpPage>
</template>
